import { Stack } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { TextSerif } from "@/components/elements";
import { DeleteCarForm } from "@/components/forms";
import { useDeleteCar } from "@/services/cars/delete-car";
import { showCustomNotification } from "@/utils/notifications";

import type { DeleteCarInput } from "@/services/cars/delete-car";
import type { Car } from "@/types/car";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type DeleteCarProps = {
  autoCloseDelay?: boolean | number;
  car: Car;
  garageId: string;
  onClose?: () => void;
  onSuccess?: (carId: string) => void;
} & MantineStyleProps;

export const DeleteCar: FC<DeleteCarProps> = ({
  autoCloseDelay = false,
  car,
  garageId,
  onClose,
  onSuccess,
  ...props
}) => {
  const { t } = useTranslation();
  const { mutate, isPending } = useDeleteCar({
    mutationConfig: { onSuccess: handleSuccess },
  });
  const queryClient = useQueryClient();

  function handleSuccess() {
    showCustomNotification({
      autoClose: autoCloseDelay || 6000,
      title: t("components.DeleteCar.success.title"),
      message: t("components.DeleteCar.success.message"),
    });
    onClose?.();
    onSuccess?.(car.identifier);
  }

  function handleSubmit(data: DeleteCarInput) {
    mutate({ carId: car.identifier, garageId, data });
    queryClient.invalidateQueries({ queryKey: ["cars"] });
  }

  return (
    <Stack {...props}>
      <TextSerif style={{ whiteSpace: "pre-line" }}>{t("components.DeleteCar.message")}</TextSerif>
      <DeleteCarForm loading={isPending} onCancel={onClose} onSubmit={handleSubmit} />
    </Stack>
  );
};
